import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import { cn } from "@ds/lib/utils";

/**
 * Dialog — the pointer presentation of a sheet.
 *
 * DESIGNED, NOT OBSERVED. The captured product is phone-only, so nothing in the
 * evidence says what a bottom sheet becomes on a wide screen (capture field
 * 25.10). A centred dialog is the conventional answer and is chosen here
 * deliberately — treat it as a decision to review, not a finding.
 */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogTitle = DialogPrimitive.Title;
const DialogDescription = DialogPrimitive.Description;

function DialogContent({
  className,
  children,
  closeLabel = "Close",
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Popup> & {
  /** Accessible name of the close control. */
  closeLabel?: string;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Backdrop
        className={cn(
          "fixed inset-0 z-50 bg-scrim",
          "data-open:[animation:ds-fade-up_200ms_var(--ds-ease-out)]",
        )}
      />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 flex max-h-[85svh] w-[calc(100%-2rem)] max-w-lg",
          "-translate-x-1/2 -translate-y-1/2 flex-col",
          "rounded-card border border-border bg-background p-6 outline-none",
          "data-open:[animation:ds-fade-up_200ms_var(--ds-ease-out)]",
          className,
        )}
        {...props}
      >
        <DialogPrimitive.Close
          className={cn(
            "absolute top-6 right-6 rounded-pill p-1 text-foreground",
            "transition-colors duration-200 ease-out-ds hover:bg-muted",
            "focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2 outline-none",
          )}
        >
          <X className="size-5" />
          <span className="sr-only">{closeLabel}</span>
        </DialogPrimitive.Close>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </DialogPrimitive.Popup>
    </DialogPrimitive.Portal>
  );
}

export { Dialog, DialogTrigger, DialogClose, DialogContent, DialogTitle, DialogDescription };
